import { z } from 'zod';
import { optionalText, uuidSchema } from './common.js';

export const LEDGER_ACCOUNT_TYPES = [
  'CASH',
  'BANK',
  'ACCOUNTS_RECEIVABLE',
  'OTHER_CURRENT_ASSET',
  'FIXED_ASSET',
  'OTHER_ASSET',
  'ACCOUNTS_PAYABLE',
  'OTHER_CURRENT_LIABILITY',
  'LONG_TERM_LIABILITY',
  'EQUITY',
  'INCOME',
  'OTHER_INCOME',
  'COST_OF_GOODS_SOLD',
  'EXPENSE',
  'OTHER_EXPENSE',
] as const;
export type LedgerAccountTypeName = (typeof LEDGER_ACCOUNT_TYPES)[number];

export const createLedgerAccountSchema = z.object({
  code: z.string().trim().max(20).nullish(),
  name: z.string().trim().min(1, 'Required').max(150),
  // A bank or cash account is created in Banking, which gives it its ledger account.
  type: z.enum(LEDGER_ACCOUNT_TYPES).refine((t) => t !== 'BANK', 'Add bank accounts under Banking'),
  description: optionalText(1000),
});
export type CreateLedgerAccountInput = z.infer<typeof createLedgerAccountSchema>;

export const updateLedgerAccountSchema = z.object({
  code: z.string().trim().max(20).nullish(),
  name: z.string().trim().min(1).max(150).optional(),
  description: optionalText(1000),
  isActive: z.boolean().optional(),
});
export type UpdateLedgerAccountInput = z.infer<typeof updateLedgerAccountSchema>;

const amount = z.coerce.number().min(0).max(1e15);

export const manualJournalSchema = z.object({
  date: z.iso.date(),
  description: z.string().trim().min(1, 'Required').max(500),
  /** A year-end adjustment, posted to the 13th period. */
  isAdjustmentPeriod: z.boolean().default(false),
  lines: z
    .array(
      z.object({
        ledgerAccountId: uuidSchema,
        debit: amount.default(0),
        credit: amount.default(0),
        description: z.string().trim().max(500).nullish(),
      }),
    )
    .min(2, 'A journal needs at least two lines')
    .max(200),
});
export type ManualJournalInput = z.infer<typeof manualJournalSchema>;

export const voidJournalSchema = z.object({ reason: z.string().trim().min(1, 'Say why').max(500) });

export const budgetSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(150),
  fiscalYear: z.coerce.number().int().min(2000).max(2100),
  lines: z
    .array(
      z.object({
        ledgerAccountId: uuidSchema,
        month: z.coerce.number().int().min(1).max(12),
        amount: z.coerce.number().min(-1e15).max(1e15),
      }),
    )
    .max(2400),
});
export type BudgetInput = z.infer<typeof budgetSchema>;

export interface LedgerAccountView {
  id: string;
  code: string | null;
  name: string;
  type: LedgerAccountTypeName;
  description: string | null;
  isSystem: boolean;
  isActive: boolean;
  bankAccountId: string | null;
  /** Closing balance, debit-positive for asset and expense accounts, credit-positive otherwise. */
  balance: string;
}

export interface JournalView {
  id: string;
  number: string;
  date: string;
  description: string;
  source: string;
  sourceType: string | null;
  sourceId: string | null;
  isAdjustmentPeriod: boolean;
  voided: boolean;
  voidReason: string | null;
  lines: {
    account: { id: string; name: string };
    debit: string;
    credit: string;
    description: string | null;
  }[];
}

export interface BudgetView {
  id: string;
  name: string;
  fiscalYear: number;
  lines: { ledgerAccountId: string; accountName: string; month: number; amount: string }[];
}

export interface LedgerSyncSummary {
  posted: number;
  superseded: number;
  unchanged: number;
  skipped: { sourceType: string; sourceId: string; label: string; reason: string }[];
}
